import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, GitBranch, FileCheck, Search, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { PROJECTS, PROJECT_STATUS_COLORS } from "../config/wiseboardConfig";

interface TimelineEvent {
  id: string;
  timestamp: string;
  type: "project_status" | "artifact" | "evidence";
  title: string;
  detail: string;
  status?: string;
}

interface CaseTimelineProps {
  caseId: string;
  refreshKey?: number;
}

const EVENT_ICONS = {
  project_status: GitBranch,
  artifact: FileCheck,
  evidence: Search,
};

const EVENT_COLORS = {
  project_status: "text-blue-500",
  artifact: "text-amber-500",
  evidence: "text-emerald-500",
};

export default function CaseTimeline({ caseId, refreshKey }: CaseTimelineProps) {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [projRes, artRes, evRes] = await Promise.all([
        supabase
          .from("wiseboard_projects")
          .select("id, project_type, status, updated_at, started_at, completed_at")
          .eq("case_id", caseId)
          .order("updated_at", { ascending: false }),
        supabase
          .from("wiseboard_artifacts")
          .select("id, name, approval_status, version, created_at, project_id")
          .order("created_at", { ascending: false }),
        supabase
          .from("wiseboard_evidence_matrix")
          .select("id, claim, status, confidence, updated_at, hypothesis_id")
          .eq("case_id", caseId)
          .order("updated_at", { ascending: false }),
      ]);

      const projectIds = (projRes.data || []).map((p) => p.id);
      const timeline: TimelineEvent[] = [];

      // Project status events
      (projRes.data || []).forEach((p) => {
        const label = PROJECTS[p.project_type]?.name || p.project_type;
        timeline.push({
          id: `proj-${p.id}`,
          timestamp: p.updated_at,
          type: "project_status",
          title: `${p.project_type.replace("_", " ")} — ${label}`,
          detail: `Status → ${p.status.replace("_", " ")}`,
          status: p.status,
        });
      });

      // Artifact events (filter to this case's projects)
      (artRes.data || [])
        .filter((a) => projectIds.includes(a.project_id))
        .forEach((a) => {
          timeline.push({
            id: `art-${a.id}`,
            timestamp: a.created_at,
            type: "artifact",
            title: a.name,
            detail: `v${a.version} · ${a.approval_status}`,
            status: a.approval_status,
          });
        });

      // Evidence events
      (evRes.data || []).forEach((e) => {
        timeline.push({
          id: `ev-${e.id}`,
          timestamp: e.updated_at,
          type: "evidence",
          title: e.claim.length > 60 ? e.claim.slice(0, 57) + "…" : e.claim,
          detail: `${e.status.replace("_", " ")}${e.confidence != null ? ` · ${Math.round(e.confidence * 100)}%` : ""}`,
          status: e.status,
        });
      });

      timeline.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      setEvents(timeline);
      setLoading(false);
    };
    load();
  }, [caseId, refreshKey]);

  const visible = expanded ? events : events.slice(0, 8);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <Clock className="h-4 w-4" /> Case Timeline
          </CardTitle>
          <Badge variant="secondary" className="text-xs">{events.length} events</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading timeline…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
        ) : (
          <>
            <div className="relative ml-3 border-l-2 border-muted pl-6 space-y-4">
              {visible.map((ev) => {
                const Icon = EVENT_ICONS[ev.type];
                const color = EVENT_COLORS[ev.type];
                return (
                  <div key={ev.id} className="relative">
                    <div className={`absolute -left-[31px] top-0.5 rounded-full bg-background border-2 border-muted p-1 ${color}`}>
                      <Icon className="h-3 w-3" />
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(ev.timestamp), "MMM d, HH:mm")}
                      </span>
                      <span className="font-medium text-sm">{ev.title}</span>
                      {ev.status && (
                        <Badge variant="outline" className={`text-[10px] ${PROJECT_STATUS_COLORS[ev.status] || ""}`}>
                          {ev.detail}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {events.length > 8 && (
              <Button variant="ghost" size="sm" className="mt-3 w-full" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                {expanded ? "Show less" : `Show all ${events.length} events`}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
